/**
 * GET /api/v1/customers (v0.1.58): the Müşteriler view. Sercan, 2026-09-29: "tüm müşterileri
 * kaydedelim; ileride bu isimle bu adam bu ürünü alırsa takip etmiş oluruz; GrabCAD sayesinde
 * gelen 10 mailin 2'si satışa döndü gibi bir istatistik sayfamız olmalı". Per source (where the
 * first contact came from): conversations, the ones answered, the ones that turned into an
 * order, orders and revenue. Per customer: the address they write from (or an Etsy handle, or
 * a buyer hash for an order without any conversation), the vehicles they asked about and a
 * timeline of messages, decisions and orders. Panel only; the bot never reads it. Instants are
 * epoch milliseconds, money is cents.
 */
import { z } from 'zod';
const instant = z.number().int().nonnegative();
const count = z.number().int().nonnegative();
/** Where a customer's first contact came from; etsy_order is an order with no conversation on record. */
export const CUSTOMER_SOURCES = [
    'gmail',
    'grabcad',
    'web_form',
    'lead',
    'etsy_message',
    'whatsapp',
    'etsy_order',
];
export const CUSTOMER_SOURCE_LABELS_TR = Object.freeze({
    gmail: 'Doğrudan e-posta',
    grabcad: 'GrabCAD',
    web_form: 'Web formu',
    lead: 'Lead cevabı',
    etsy_message: 'Etsy mesajı',
    whatsapp: 'WhatsApp',
    etsy_order: 'Etsy, yazışmasız',
});
export const customerSourceStatSchema = z.object({
    source: z.enum(CUSTOMER_SOURCES),
    label: z.string(),
    /** Conversations that started from this source. */
    conversations: count,
    /** Of those, the ones with at least one reply from us. */
    replied: count,
    /** Of those, the ones whose customer ordered after writing. */
    converted: count,
    /** Orders attributed to this source (the customer's newest conversation before the order). */
    orders: count,
    revenueCents: count,
});
export const CUSTOMER_EVENT_KINDS = ['in', 'out', 'order', 'approved', 'edited', 'rejected', 'own_reply'];
export const customerEventSchema = z.object({
    id: z.string(),
    at: instant,
    kind: z.enum(CUSTOMER_EVENT_KINDS),
    /** Turkish, one line: "Müşteri yazdı", "Cevap gitti", "Sipariş · Ödendi", "Taslak onaylandı". */
    title: z.string(),
    /** The message (Turkish rendering when there is one), the order lines or the decision note; clipped. */
    text: z.string().nullable(),
    amountCents: count.nullable(),
    conversationId: z.string().nullable(),
});
export const customerSchema = z.object({
    /** Stable within the view: the e-mail hash, a handle key or a buyer hash key. */
    id: z.string(),
    /** The address they write from, the Etsy handle, or "Etsy alıcısı <hash8>" for an order-only buyer. */
    name: z.string(),
    sources: z.array(z.enum(CUSTOMER_SOURCES)),
    countryCode: z.string().nullable(),
    /** The shop's vehicle words found in what they wrote or bought. */
    vehicles: z.array(z.string()),
    firstSeenAt: instant,
    lastSeenAt: instant,
    conversations: count,
    replied: z.boolean(),
    orders: count,
    revenueCents: count,
    /** Wrote first, ordered later. */
    converted: z.boolean(),
    timeline: z.array(customerEventSchema),
});
export const customersViewSchema = z.object({
    asOf: instant,
    totals: z.object({
        customers: count,
        conversations: count,
        replied: count,
        converted: count,
        orders: count,
        revenueCents: count,
    }),
    sources: z.array(customerSourceStatSchema),
    customers: z.array(customerSchema),
});
//# sourceMappingURL=customers.js.map